import { useState, useEffect, useCallback } from "react";
import { adminApi, type AnnouncementRow, type AdminRow } from "@/lib/superadmin";
import { useAdmin } from "@/lib/superadmin";
import { PageHeader } from "../components/ui";

function fmtDate(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const LEVELS: AnnouncementRow["level"][] = ["info", "warning", "critical"];
const AUDIENCES = ["all", "trial", "active", "suspended", "starter", "growth", "pro"];

const EMPTY: AnnouncementRow = {
  title: "",
  body: "",
  level: "info",
  audience: "all",
  active: true,
  startsAt: null,
  endsAt: null,
};

const levelBadge: Record<string, string> = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-accent-100 text-accent-700",
};

export default function AnnouncementsPage() {
  const [tab, setTab] = useState<"announcements" | "admins">("announcements");
  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Announcements & Admins"
        subtitle="Broadcast banners to tenants and manage who can access this panel"
      />

      <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-background-100 border border-background-200/70">
        {([
          { key: "announcements", label: "Announcements", icon: "ri-megaphone-line" },
          { key: "admins", label: "Platform Admins", icon: "ri-shield-user-line" },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              tab === t.key
                ? "bg-background-50 text-foreground-900 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className={`${t.icon} text-sm`}></i>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "announcements" ? <AnnouncementsTab /> : <AdminsTab />}
    </div>
  );
}

function AnnouncementsTab() {
  const [rows, setRows] = useState<AnnouncementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<AnnouncementRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.announcements.list();
      setRows(r.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load announcements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setIsNew(true);
  };
  const openEdit = (a: AnnouncementRow) => {
    setEditing({
      ...a,
      startsAt: a.startsAt ? a.startsAt.slice(0, 10) : null,
      endsAt: a.endsAt ? a.endsAt.slice(0, 10) : null,
    });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing || !editing.title.trim() || !editing.body.trim()) return;
    setSaving(true);
    try {
      await adminApi.announcements.upsert({
        ...editing,
        startsAt: editing.startsAt ? new Date(editing.startsAt).toISOString() : null,
        endsAt: editing.endsAt ? new Date(editing.endsAt).toISOString() : null,
      });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id?: string) => {
    if (!id || !confirm("Delete this announcement?")) return;
    try {
      await adminApi.announcements.delete(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <button
          onClick={() => void load()}
          className="px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200 hover:bg-background-100 rounded-md flex items-center gap-2 cursor-pointer"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>Refresh
        </button>
        <button
          onClick={openNew}
          className="px-4 py-2.5 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-add-line"></i>New announcement
        </button>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-accent-100 text-accent-800 text-sm">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={() => void load()} className="font-semibold hover:underline cursor-pointer">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
        </div>
      ) : rows.length ? (
        <div className="space-y-3">
          {rows.map((a) => (
            <div key={a.id} className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${levelBadge[a.level]}`}>{a.level}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600">{a.audience}</span>
                    {!a.active && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-200 text-foreground-500">Off</span>}
                  </div>
                  <p className="font-semibold text-foreground-900 mt-2">{a.title}</p>
                  <p className="text-sm text-foreground-600 mt-1">{a.body}</p>
                  <p className="text-[11px] text-foreground-400 mt-2">
                    {fmtDate(a.startsAt)} → {fmtDate(a.endsAt)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button onClick={() => openEdit(a)} className="text-sm text-primary-600 hover:underline cursor-pointer">Edit</button>
                  <button onClick={() => void remove(a.id)} className="text-sm text-accent-600 hover:underline cursor-pointer">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200 rounded-lg py-12 text-center">
          <i className="ri-megaphone-line text-3xl text-foreground-300"></i>
          <p className="text-sm text-foreground-400 mt-2">No announcements yet.</p>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-background-50 rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-heading font-bold text-foreground-900">{isNew ? "New announcement" : "Edit announcement"}</h2>
            <label className="text-sm block">
              <span className="text-foreground-600">Title</span>
              <input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
              />
            </label>
            <label className="text-sm block">
              <span className="text-foreground-600">Message</span>
              <textarea
                value={editing.body}
                rows={3}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 resize-none"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-foreground-600">Level</span>
                <select
                  value={editing.level}
                  onChange={(e) => setEditing({ ...editing, level: e.target.value as AnnouncementRow["level"] })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer capitalize"
                >
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Audience</span>
                <select
                  value={editing.audience}
                  onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer capitalize"
                >
                  {AUDIENCES.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Starts</span>
                <input
                  type="date"
                  value={editing.startsAt ?? ""}
                  onChange={(e) => setEditing({ ...editing, startsAt: e.target.value || null })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Ends</span>
                <input
                  type="date"
                  value={editing.endsAt ?? ""}
                  onChange={(e) => setEditing({ ...editing, endsAt: e.target.value || null })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
            </div>
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-foreground-600">Active (visible to tenants)</span>
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer">Cancel</button>
              <button
                onClick={() => void save()}
                disabled={saving || !editing.title.trim() || !editing.body.trim()}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminsTab() {
  const { me } = useAdmin();
  const [rows, setRows] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ uid: "", email: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.admins.list();
      setRows(r.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load admins.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    if (!form.uid.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      await adminApi.admins.add(form);
      setAdding(false);
      setForm({ uid: "", email: "", name: "", role: "admin" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Add failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (uid: string, email: string) => {
    if (!confirm(`Remove admin ${email}? They lose access to /superadmin.`)) return;
    try {
      await adminApi.admins.remove(uid);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 text-foreground-500 text-xs">
        <i className="ri-information-line"></i>
        The user must already exist in Firebase Auth. Find their UID in the Firebase console → Authentication, then add it here.
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={() => void load()}
          className="px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200 hover:bg-background-100 rounded-md flex items-center gap-2 cursor-pointer"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>Refresh
        </button>
        <button
          onClick={() => setAdding(true)}
          className="px-4 py-2.5 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-user-add-line"></i>Add admin
        </button>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-accent-100 text-accent-800 text-sm">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={() => void load()} className="font-semibold hover:underline cursor-pointer">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">UID</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {rows.map((a) => (
                  <tr key={a.uid} className="hover:bg-background-100/40">
                    <td className="px-4 py-3 text-foreground-800 font-medium">
                      {a.email}
                      {a.uid === me.uid && <span className="ml-2 text-[10px] text-foreground-400">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-foreground-600">{a.name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 capitalize">{a.role}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-foreground-400">{a.uid}</td>
                    <td className="px-4 py-3 text-right">
                      {a.uid !== me.uid ? (
                        <button onClick={() => void remove(a.uid, a.email)} className="text-sm text-accent-600 hover:underline cursor-pointer">Remove</button>
                      ) : (
                        <span className="text-xs text-foreground-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 p-4" onClick={() => setAdding(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-heading font-bold text-foreground-900">Add platform admin</h2>
            <label className="text-sm block">
              <span className="text-foreground-600">Firebase UID</span>
              <input
                value={form.uid}
                onChange={(e) => setForm({ ...form, uid: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 font-mono"
              />
            </label>
            <label className="text-sm block">
              <span className="text-foreground-600">Email</span>
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-foreground-600">Name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Role</span>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer"
                >
                  <option value="owner">owner</option>
                  <option value="admin">admin</option>
                  <option value="support">support</option>
                </select>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setAdding(false)} className="px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer">Cancel</button>
              <button
                onClick={() => void add()}
                disabled={saving || !form.uid.trim() || !form.email.trim()}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
